import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";

/**
 * THE END CARD'S GEOMETRY — one shell for every game's result card (solo and online).
 *
 * The operator, 2026-10-08: «The results screen must not cover what players still need — e.g. the
 * hand counts in حکم and بی‌دل — and must look right on every phone size.» Each of the nine games
 * used to carry its own copy of one full-screen dim (`fixed; inset:0; z-58; padding:20`), with no
 * safe area and no height limit. So the table's header band — the exit, the title and the score box
 * that holds حکم's «دست n | ما | حریف» and بی‌دل's four totals — was darkened at every size and
 * covered on short ones, the card started under the status bar and Telegram's own header, and on
 * the smallest Telegram-Desktop windows it ran off the screen with «خروج» out of reach.
 *
 * This component owns ONLY that geometry; every game's overlay keeps its own content, copy and look
 * (the dim's colour, the card's surface, border, glow and width ride in as props):
 *
 *  - THE DIM AND THE CARD START AT THE BOTTOM OF THE TABLE'S HEADER BAND. The band is MEASURED, never
 *    assumed: the nearest `[data-table-hud]` (a table whose band is not a `<header>` marks it), else
 *    the nearest `<header>`, outside this shell — re-measured when its border box resizes, when a
 *    safe-area inset lands on `<html>`, and when the window or the visual viewport resizes (each
 *    once more on the next frame). So the band is never covered and never dimmed, and its controls
 *    stay usable (the table's own exit is the plain leave once the match is over). No band on
 *    screen → the safe-top inset.
 *  - The card is centred in the region below the band and CAPPED to it, minus the bottom safe area;
 *    its content scrolls inside it, and the action row («بازی دوباره» / «خروج», with its hint line)
 *    sits OUTSIDE the scroll — it can be neither scrolled away nor cut off.
 *  - Every inset goes through `max(env(safe-area-inset-*), var(--tg-safe-*))`: Telegram's fullscreen
 *    zeroes `env()` and publishes the real numbers on `<html>` instead.
 *  - Short screens tighten the paddings (`vh`-capped, so a tall phone keeps the designed 20 / 30 px);
 *    nothing else about the card changes.
 *
 * ⚠️ It is NOT a portal: the dim renders where the overlay always did, beside the table, so its
 * z-index (58 — above every table layer, seats z-20, HUD z-40/50 and the chat toggle z-55, and below
 * the chat sheet and the confirm dialog at z-70) keeps the stacking it was tuned in.
 */

/** The four safe-area insets, each through the app's one expression (CLAUDE.md, Gotchas). */
const SAFE_TOP = "max(env(safe-area-inset-top), var(--tg-safe-top, 0px))";
const SAFE_RIGHT = "max(env(safe-area-inset-right), var(--tg-safe-right, 0px))";
const SAFE_BOTTOM = "max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px))";
const SAFE_LEFT = "max(env(safe-area-inset-left), var(--tg-safe-left, 0px))";

/**
 * The paddings, as designed (the dim's 20, the card's 30 × 24) and TIGHTENED ON SHORT SCREENS ONLY:
 * the block-direction ones are capped at a share of the viewport height that equals the design at
 * 800 px, so every phone from 384×823 up is unchanged and a 393-px Telegram-Desktop window gives the
 * card's content ≈ 30 px more before it has to scroll.
 */
const DIM_PAD_BLOCK = "min(20px, 2.5vh)";
const DIM_PAD_INLINE = "20px";
const CARD_PAD_BLOCK = "min(30px, 3.75vh)";
const CARD_PAD_INLINE = "24px";

/** Where a table's header band is, most explicit first. */
const BAND_SELECTORS = ["[data-table-hud]", "header"] as const;

/**
 * The table's header band: searched outward from the shell, so it is the band of the table this
 * card sits beside — and never anything inside the card itself (a promo may bring its own heading).
 */
function findBand(shell: HTMLElement, selectors: readonly string[]): HTMLElement | null {
  for (let scope = shell.parentElement; scope; scope = scope.parentElement) {
    for (const selector of selectors) {
      for (const el of scope.querySelectorAll<HTMLElement>(selector)) {
        if (!shell.contains(el)) return el;
      }
    }
  }
  return null;
}

/**
 * The band's bottom edge in viewport pixels, or null when there is no band to measure (none on
 * screen, or one with no height — hidden, or not laid out yet). Measured before paint, so the dim
 * never first covers the band and then jumps. `selectors` (a module constant — it is an effect
 * dependency) names the band: the end card's {@link BAND_SELECTORS} by default; the maintenance
 * warning at a table (`MaintenanceWarningNotice`) asks for the bands that opt in to its clearance
 * (card-98 item 13).
 */
export function useBandBottom(
  shellRef: RefObject<HTMLElement>,
  selectors: readonly string[] = BAND_SELECTORS,
): number | null {
  const [bottom, setBottom] = useState<number | null>(null);
  useLayoutEffect(() => {
    const shell = shellRef.current;
    const band = shell ? findBand(shell, selectors) : null;
    if (!band) return undefined;
    const measure = (): void => {
      const rect = band.getBoundingClientRect();
      setBottom(rect.height > 0 ? rect.bottom : null);
    };
    // Every trigger measures at once AND on the next frame: the layout can settle a frame late.
    let frame: number | null = null;
    const remeasure = (): void => {
      measure();
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        measure();
      });
    };
    measure();
    // The band can grow or move WITHOUT its content box changing (staging, 2026-10-08: dim top 50
    // vs band bottom 74): a late safe-area inset grows a header by PADDING, and an ancestor's inset
    // moves a band without resizing it. So: the band's BORDER box (the score box wrapping, too);
    // `<html>`, where `telegram/viewport.ts` publishes `--tg-safe-*` (a late safe-area event, a
    // fullscreen toggle); and the window and the visual viewport (a rotation, a Desktop resize).
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(remeasure) : null;
    observer?.observe(band, { box: "border-box" });
    const rootObserver = new MutationObserver(remeasure);
    rootObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style", "class", "data-theme"],
    });
    const visual = window.visualViewport;
    window.addEventListener("resize", remeasure);
    visual?.addEventListener("resize", remeasure);
    return () => {
      observer?.disconnect();
      rootObserver.disconnect();
      window.removeEventListener("resize", remeasure);
      visual?.removeEventListener("resize", remeasure);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [shellRef, selectors]);
  return bottom;
}

export function EndCardShell({
  label,
  testId,
  scrim,
  cardClassName,
  cardStyle,
  actions,
  children,
}: {
  /** The dialog's accessible name — the game's «پایان بازی». */
  label: string;
  /** A `data-testid` for the dim, where a game's tests already find its card by one. */
  testId?: string;
  /** The dim's colour (each game keeps its own). */
  scrim: string;
  /** The card's classes — `end-card` and its outcome variant (the shared entrance animation). */
  cardClassName: string;
  /**
   * The card's LOOK — surface, border, radius, glow, width cap, text alignment, the gap between its
   * content and its actions. Never geometry: the shell's own display, height cap and paddings win.
   */
  cardStyle: CSSProperties;
  /** The action row — «بازی دوباره» / «خروج» and the line under them. Never scrolls. */
  actions: ReactNode;
  /** The result itself — trophy, title, reason, scores. Scrolls when the screen is too short. */
  children: ReactNode;
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const bandBottom = useBandBottom(ref);
  return (
    <div
      ref={ref}
      className="end-overlay"
      role="dialog"
      aria-label={label}
      data-testid={testId}
      style={{
        position: "fixed",
        top: bandBottom === null ? SAFE_TOP : `${bandBottom}px`,
        right: 0,
        bottom: 0,
        left: 0,
        // ABOVE every table layer (seats z-20, HUD z-40/45/50, chat toggle z-55): without an
        // explicit z the fixed overlay is z-auto and the positioned seat profiles paint THROUGH the
        // result card (user report 2026-07-10). 58 keeps the chat sheet (z-70) above the dim.
        zIndex: 58,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: `${DIM_PAD_BLOCK} calc(${DIM_PAD_INLINE} + ${SAFE_RIGHT}) calc(${DIM_PAD_BLOCK} + ${SAFE_BOTTOM}) calc(${DIM_PAD_INLINE} + ${SAFE_LEFT})`,
        background: scrim,
      }}
    >
      <div
        className={cardClassName}
        style={{
          ...cardStyle,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          // The dim's width less its gutters, capped by the card's own max — and NOTHING inside may
          // stretch it: at 256 px one 16-character Latin name used to widen the row, the row the
          // card, and the card ran 54 px off the screen (staging, 2026-10-08). `min-width: 0` down
          // the chain (card, content, actions) keeps every flex minimum from reaching for content.
          width: "100%",
          minWidth: 0,
          // The region below the band, less the dim's padding (which carries the bottom inset).
          maxHeight: "100%",
          // …and allowed to BE that small: a flex item's automatic minimum is its content.
          minHeight: 0,
          boxSizing: "border-box",
          // The block padding lives on the two parts, so the scroll runs edge to edge of the card.
          padding: `0 ${CARD_PAD_INLINE}`,
        }}
      >
        <div
          data-end-card-body=""
          style={{
            flex: "0 1 auto",
            minHeight: 0,
            minWidth: 0,
            overflowX: "hidden",
            overflowY: "auto",
            overscrollBehavior: "contain",
            paddingTop: CARD_PAD_BLOCK,
          }}
        >
          {children}
        </div>
        <div
          data-end-card-actions=""
          style={{ flex: "none", minWidth: 0, paddingBottom: CARD_PAD_BLOCK }}
        >
          {actions}
        </div>
      </div>
    </div>
  );
}
